import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * MKS-274 — full-size image overlay for attachments.
 *
 * Rendered into `document.body` via `createPortal` so no scroll container
 * (the task slide-over, a comment list) can clip it, and sized to the viewport
 * (`fixed inset-0` + `object-contain`) so the image shows at its full
 * resolution scaled down to fit rather than at a thumbnail size.
 *
 * Closes on Esc and on a click on the backdrop. The Esc listener runs in the
 * CAPTURE phase and stops propagation, because `TaskDetailSlideOver` carries
 * its own document-level Escape listener that would otherwise close the whole
 * panel behind the overlay.
 */
export interface ImageLightboxProps {
  src: string;
  alt: string;
  onClose: () => void;
  testId?: string;
}

export function ImageLightbox({ src, alt, onClose, testId = 'image-lightbox' }: ImageLightboxProps) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  // Keep the page behind the overlay from scrolling under the pointer.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const overlay = (
    <div
      className="fixed inset-0 z-[90] flex flex-col bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      data-testid={testId}
      onClick={(e) => {
        // Only a click on the backdrop itself (not on the image or the
        // toolbar) closes the overlay.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex flex-shrink-0 items-center justify-end gap-2 pb-2">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs font-medium text-white hover:bg-white/20"
          data-testid={`${testId}-original`}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
          Open original
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close image preview"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20"
          data-testid={`${testId}-close`}
        >
          <XMarkIcon className="h-5 w-5" aria-hidden />
        </button>
      </div>
      <div
        className="flex min-h-0 flex-1 items-center justify-center"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <img
          src={src}
          alt={alt}
          className="max-h-full max-w-full object-contain"
          data-testid={`${testId}-image`}
        />
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
